import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { budgetsAPI, departmentsAPI, financialYearsAPI } from '../api/client';
import { usePermissions } from '../context/PermissionsContext';
import { Plus, Edit2, Trash2, Search, Calendar, Layers, CalendarRange, ChevronUp, ChevronDown, Copy, CheckCircle, XCircle, Clock, Send, AlertTriangle } from 'lucide-react';
import TablePagination from '../components/TablePagination';

const BUDGET_PAGE_SIZE = 25;
const fmt = (n) => (n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 });

export default function BudgetHeaders() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [budgets, setBudgets] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [budgetPage, setBudgetPage] = useState(1);

  // Clone modal state
  const [cloneSource, setCloneSource] = useState(null);
  const [cloneForm, setCloneForm] = useState({ targetFinancialYearId: '', newBudgetName: '', adjustmentType: 'none', adjustmentValue: 0 });
  const [cloning, setCloning] = useState(false);

  // Approval modal state
  const [approvalBudget, setApprovalBudget] = useState(null);
  const [approvalAction, setApprovalAction] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approvalSaving, setApprovalSaving] = useState(false);
  const [approvalError, setApprovalError] = useState('');

  const [formData, setFormData] = useState({
    budgetName: '',
    budgetType: 'Departmental',
    departmentId: '',
    financialYearId: '',
    totalBudget: '',
    status: 'Draft'
  });

  const canManage = hasPermission('manage_budgets');

  useEffect(() => {
    fetchBudgets();
    fetchDepartments();
    fetchFinancialYears();
  }, [filterYear]);

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterYear) params.fiscalYear = filterYear;
      const response = await budgetsAPI.getAll(params);
      setBudgets(response.data.items || []);
    } catch (err) {
      console.error('Error fetching budgets:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await departmentsAPI.getAll();
      const depts = response.data.departments || response.data.items || response.data || [];
      setDepartments(Array.isArray(depts) ? depts : []);
    } catch (err) {
      console.error('Error fetching departments:', err);
      setDepartments([]);
    }
  };

  const fetchFinancialYears = async () => {
    try {
      const response = await financialYearsAPI.getAll();
      const years = response.data.items || response.data.financialYears || response.data || [];
      setFinancialYears(Array.isArray(years) ? years : []);
    } catch (err) {
      console.error('Error fetching financial years:', err);
      setFinancialYears([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBudget) {
        await budgetsAPI.update(editingBudget.BudgetID, formData);
      } else {
        await budgetsAPI.create(formData);
      }
      setShowModal(false);
      resetForm();
      fetchBudgets();
    } catch (err) {
      console.error('Error saving budget:', err);
      alert(err.response?.data?.message || 'Failed to save budget');
    }
  };

  const handleDelete = async (budgetId) => {
    if (!window.confirm('Are you sure you want to delete this budget header?')) return;
    try {
      await budgetsAPI.delete(budgetId);
      fetchBudgets();
    } catch (err) {
      console.error('Error deleting budget:', err);
      alert('Failed to delete budget');
    }
  };

  const openCreateModal = () => {
    resetForm();
    setEditingBudget(null);
    setShowModal(true);
  };

  const openEditModal = (budget) => {
    setEditingBudget(budget);
    setFormData({
      budgetName: budget.BudgetName || '',
      budgetType: budget.BudgetType || 'Departmental',
      departmentId: budget.DepartmentID || '',
      financialYearId: budget.FinancialYearID || '',
      totalBudget: budget.TotalBudget || '',
      status: budget.Status || 'Draft'
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ budgetName: '', budgetType: 'Departmental', departmentId: '', financialYearId: '', totalBudget: '', status: 'Draft' });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const openCloneModal = (budget) => {
    setCloneSource(budget);
    setCloneForm({ targetFinancialYearId: '', newBudgetName: `${budget.BudgetName} (Copy)`, adjustmentType: 'none', adjustmentValue: 0 });
  };

  const handleClone = async () => {
    if (!cloneForm.targetFinancialYearId) { alert('Please select a target financial year'); return; }
    try {
      setCloning(true);
      await budgetsAPI.clone(cloneSource.BudgetID, {
        targetFinancialYearId: cloneForm.targetFinancialYearId,
        newBudgetName: cloneForm.newBudgetName,
        amountAdjustment: cloneForm.adjustmentType === 'percentage'
          ? { type: 'percentage', value: parseFloat(cloneForm.adjustmentValue) || 0 }
          : cloneForm.adjustmentType === 'fixed'
          ? { type: 'fixed', value: parseFloat(cloneForm.adjustmentValue) || 0 }
          : null
      });
      setCloneSource(null);
      fetchBudgets();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to clone budget');
    } finally {
      setCloning(false);
    }
  };

  const openApprovalModal = (budget, action) => {
    setApprovalBudget(budget);
    setApprovalAction(action);
    setApprovalNotes('');
    setApprovalError('');
  };

  const handleApproval = async () => {
    if (!approvalBudget) return;
    try {
      setApprovalSaving(true);
      if (approvalAction === 'submit') {
        await budgetsAPI.submitForApproval(approvalBudget.BudgetID, approvalNotes);
      } else if (approvalAction === 'approve') {
        await budgetsAPI.approve(approvalBudget.BudgetID, approvalNotes);
      } else if (approvalAction === 'reject') {
        await budgetsAPI.reject(approvalBudget.BudgetID, approvalNotes);
      }
      setApprovalBudget(null);
      fetchBudgets();
    } catch (err) {
      setApprovalError(err.response?.data?.error || err.response?.data?.message || 'Action failed — please try again');
    } finally {
      setApprovalSaving(false);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-600" /> : <ChevronDown className="w-3 h-3 text-blue-600" />;
  };

  const getStatusBadge = (status) => {
    const styles = {
      Draft: 'bg-gray-100 text-gray-700',
      Submitted: 'bg-amber-100 text-amber-800',
      Active: 'bg-green-100 text-green-800',
      Closed: 'bg-red-100 text-red-800',
      Archived: 'bg-slate-100 text-slate-600',
      Rejected: 'bg-rose-100 text-rose-700'
    };
    return styles[status] || styles.Draft;
  };

  const filteredBudgets = budgets.filter(b => {
    const matchSearch = !searchTerm ||
      b.BudgetName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.DepartmentName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = !filterStatus || b.Status === filterStatus;
    return matchSearch && matchStatus;
  });

  const sortedBudgets = [...filteredBudgets].sort((a, b) => {
    if (!sortField) return 0;
    const dir = sortDir === 'asc' ? 1 : -1;
    const numericFields = ['TotalBudget', 'TotalAllocated', 'TotalRemaining'];
    if (numericFields.includes(sortField)) {
      return ((a[sortField] || 0) - (b[sortField] || 0)) * dir;
    }
    return (a[sortField] || '').toString().localeCompare((b[sortField] || '').toString()) * dir;
  });

  const pagedBudgets = sortedBudgets.slice((budgetPage - 1) * BUDGET_PAGE_SIZE, budgetPage * BUDGET_PAGE_SIZE);
  React.useEffect(() => { setBudgetPage(1); }, [searchTerm, filterYear, filterStatus]);

  const submittedBudgets = budgets.filter(b => b.Status === 'Submitted');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Budget Headers</h1>
          <p className="text-gray-600 mt-1">Manage department and project budget allocations</p>
        </div>
        {canManage && (
          <button onClick={openCreateModal} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Create Budget Header
          </button>
        )}
      </div>

      {/* Pending Approval Banner */}
      {canManage && submittedBudgets.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">
              {submittedBudgets.length} budget{submittedBudgets.length > 1 ? 's' : ''} awaiting Finance Admin approval
            </p>
            <p className="text-sm text-amber-700 mt-0.5">
              {submittedBudgets.map(b => b.BudgetName).join(', ')}
            </p>
          </div>
          <button
            onClick={() => setFilterStatus('Submitted')}
            className="text-xs font-medium text-amber-700 underline hover:text-amber-900 flex-shrink-0"
          >
            Show only
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex gap-4 flex-wrap">
          <div className="flex-1 min-w-48">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search budgets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="w-48">
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Financial Years</option>
              {financialYears.map(fy => (
                <option key={fy.FinancialYearID} value={fy.FinancialYear}>
                  {fy.YearLabel || fy.FinancialYear}
                </option>
              ))}
            </select>
          </div>
          <div className="w-44">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="Draft">Draft</option>
              <option value="Submitted">Submitted</option>
              <option value="Active">Active</option>
              <option value="Closed">Closed</option>
              <option value="Rejected">Rejected</option>
              <option value="Archived">Archived</option>
            </select>
          </div>
        </div>
      </div>

      {/* Budgets Table */}
      <div className="card">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('BudgetName')}><div className="flex items-center gap-1">Budget Name<SortIcon field="BudgetName" /></div></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('BudgetType')}><div className="flex items-center gap-1">Type<SortIcon field="BudgetType" /></div></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('DepartmentName')}><div className="flex items-center gap-1">Department<SortIcon field="DepartmentName" /></div></th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('FinancialYear')}><div className="flex items-center justify-center gap-1">FY<SortIcon field="FinancialYear" /></div></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('TotalBudget')}><div className="flex items-center justify-end gap-1">OB (£)<SortIcon field="TotalBudget" /></div></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('TotalAllocated')}><div className="flex items-center justify-end gap-1">Spent (£)<SortIcon field="TotalAllocated" /></div></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-32">Utilization</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('Status')}><div className="flex items-center justify-center gap-1">Status<SortIcon field="Status" /></div></th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Lines</th>
                  {canManage && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pagedBudgets.map((budget) => {
                  const utilPct = budget.TotalBudget > 0
                    ? Math.min(100, ((budget.TotalAllocated || 0) / budget.TotalBudget) * 100)
                    : 0;
                  const utilColor = utilPct >= 100 ? 'bg-red-500' : utilPct >= 85 ? 'bg-amber-400' : 'bg-green-500';

                  return (
                    <tr key={budget.BudgetID} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">
                        {budget.BudgetName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {budget.BudgetType || 'Departmental'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        {budget.DepartmentName || 'Company-wide'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center text-gray-600 text-sm">
                        <div className="flex items-center justify-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {budget.YearLabel || budget.FinancialYear || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-gray-900 font-medium">
                        £{fmt(budget.TotalBudget)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-gray-600">
                        £{fmt(budget.TotalAllocated)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-16">
                            <div className={`h-2 rounded-full transition-all ${utilColor}`} style={{ width: `${utilPct}%` }} />
                          </div>
                          <span className="text-xs text-gray-500 w-10 text-right">{Math.round(utilPct)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(budget.Status)}`}>
                          {budget.Status}
                        </span>
                        {budget.Status === 'Rejected' && budget.ApprovalNotes && (
                          <p className="text-[10px] text-red-600 mt-0.5 max-w-32 truncate" title={budget.ApprovalNotes}>
                            {budget.ApprovalNotes}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => navigate(`/budget-lines?budgetId=${budget.BudgetID}`)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors"
                            title="View Budget Lines"
                          >
                            <Layers className="w-3 h-3" />
                            Lines
                          </button>
                          <button
                            onClick={() => navigate(`/budget-monthly-view?budgetId=${budget.BudgetID}`)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 rounded transition-colors"
                            title="Monthly Spread"
                          >
                            <CalendarRange className="w-3 h-3" />
                            Monthly
                          </button>
                        </div>
                      </td>
                      {canManage && (
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-1 flex-wrap">
                            <button onClick={() => openEditModal(budget)} className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded" title="Edit">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => openCloneModal(budget)} className="p-1 text-purple-600 hover:text-purple-900 hover:bg-purple-50 rounded" title="Clone to new FY">
                              <Copy className="w-4 h-4" />
                            </button>
                            {budget.Status === 'Draft' && (
                              <button onClick={() => openApprovalModal(budget, 'submit')} className="p-1 text-amber-600 hover:text-amber-900 hover:bg-amber-50 rounded" title="Submit for Approval">
                                <Send className="w-4 h-4" />
                              </button>
                            )}
                            {budget.Status === 'Submitted' && (
                              <>
                                <button onClick={() => openApprovalModal(budget, 'approve')} className="p-1 text-green-600 hover:text-green-900 hover:bg-green-50 rounded" title="Approve">
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                                <button onClick={() => openApprovalModal(budget, 'reject')} className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded" title="Reject">
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            <button onClick={() => handleDelete(budget.BudgetID)} className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {sortedBudgets.length === 0 && (
                  <tr>
                    <td colSpan={canManage ? 10 : 9} className="px-4 py-8 text-center text-gray-500">
                      No budget headers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <TablePagination page={budgetPage} pageSize={BUDGET_PAGE_SIZE} total={sortedBudgets.length} onPageChange={setBudgetPage} />
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingBudget ? 'Edit Budget Header' : 'Create Budget Header'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Budget Name *</label>
                <input type="text" required value={formData.budgetName}
                  onChange={(e) => setFormData({ ...formData, budgetName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Operations FY2025" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Budget Type *</label>
                <select required value={formData.budgetType}
                  onChange={(e) => setFormData({ ...formData, budgetType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
                  <option value="Departmental">Departmental Budget</option>
                  <option value="Business Global">Business Global Budget</option>
                  <option value="Project">Project Budget</option>
                  <option value="Programme">Programme Budget</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department (Optional)</label>
                <select value={formData.departmentId}
                  onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
                  <option value="">Company-wide</option>
                  {departments.map(dept => (
                    <option key={dept.DepartmentID} value={dept.DepartmentID}>{dept.DepartmentName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Financial Year *</label>
                <select required value={formData.financialYearId}
                  onChange={(e) => setFormData({ ...formData, financialYearId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
                  <option value="">Select financial year...</option>
                  {financialYears.map(fy => (
                    <option key={fy.FinancialYearID} value={fy.FinancialYearID}>{fy.YearLabel} ({fy.FinancialYear})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Budget (£) *</label>
                <input type="number" required step="0.01" min="0" value={formData.totalBudget}
                  onChange={(e) => setFormData({ ...formData, totalBudget: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                <select required value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
                  <option value="Draft">Draft</option>
                  <option value="Active">Active</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary flex-1">{editingBudget ? 'Update' : 'Create'}</button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Clone Modal */}
      {cloneSource && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-1">Clone Budget to New Financial Year</h2>
            <p className="text-sm text-gray-500 mb-4">Cloning: <strong>{cloneSource.BudgetName}</strong></p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Budget Name *</label>
                <input type="text" value={cloneForm.newBudgetName}
                  onChange={(e) => setCloneForm({ ...cloneForm, newBudgetName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Financial Year *</label>
                <select value={cloneForm.targetFinancialYearId}
                  onChange={(e) => setCloneForm({ ...cloneForm, targetFinancialYearId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
                  <option value="">Select target year...</option>
                  {financialYears.filter(fy => fy.FinancialYearID !== cloneSource.FinancialYearID).map(fy => (
                    <option key={fy.FinancialYearID} value={fy.FinancialYearID}>{fy.YearLabel} ({fy.FinancialYear})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount Adjustment</label>
                <select value={cloneForm.adjustmentType}
                  onChange={(e) => setCloneForm({ ...cloneForm, adjustmentType: e.target.value, adjustmentValue: 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
                  <option value="none">No adjustment (copy as-is)</option>
                  <option value="percentage">Percentage increase/decrease</option>
                  <option value="fixed">Fixed amount increase/decrease</option>
                </select>
              </div>
              {cloneForm.adjustmentType !== 'none' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {cloneForm.adjustmentType === 'percentage' ? 'Percentage (e.g. 5 for +5%, -10 for -10%)' : 'Fixed Amount (£, negative to reduce)'}
                  </label>
                  <input type="number" step={cloneForm.adjustmentType === 'percentage' ? '0.1' : '0.01'} value={cloneForm.adjustmentValue}
                    onChange={(e) => setCloneForm({ ...cloneForm, adjustmentValue: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
              )}
              <p className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded px-3 py-2">
                Overflow lines marked "Add to Next Year" will be included as Planned lines, using last year's actual spend as the new budget baseline.
              </p>
              <div className="flex gap-3 pt-2">
                <button onClick={handleClone} disabled={cloning} className="btn-primary flex-1">
                  {cloning ? 'Cloning…' : 'Clone Budget'}
                </button>
                <button onClick={() => setCloneSource(null)} className="btn-secondary flex-1" disabled={cloning}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval / Submit Modal */}
      {approvalBudget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-1">
              {approvalAction === 'submit' ? 'Submit for Approval' : approvalAction === 'approve' ? 'Approve Budget' : 'Reject Budget'}
            </h2>
            <p className="text-sm text-gray-500 mb-4"><strong>{approvalBudget.BudgetName}</strong></p>
            {approvalAction === 'submit' && (
              <p className="text-sm text-gray-600 mb-3">This will move the budget from Draft to Submitted status for Finance Admin review.</p>
            )}
            {approvalAction === 'approve' && (
              <p className="text-sm text-gray-600 mb-3">Approving will activate the budget, enabling live spend tracking against its lines.</p>
            )}
            {approvalAction === 'reject' && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
                The budget will be returned to Draft status. A rejection reason is required.
              </p>
            )}
            {approvalError && (
              <div className="mb-3 bg-red-50 border border-red-300 rounded px-3 py-2 text-sm text-red-700">
                <strong>Error:</strong> {approvalError}
                {approvalError.includes('no lines') && (
                  <p className="mt-1 text-xs text-red-600">Go to Budget Lines and add at least one planned line before submitting.</p>
                )}
              </div>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes {approvalAction === 'reject' ? '(required)' : '(optional)'}
              </label>
              <textarea rows={3} value={approvalNotes}
                onChange={(e) => { setApprovalNotes(e.target.value); setApprovalError(''); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder={approvalAction === 'reject' ? 'Reason for rejection…' : 'Optional notes…'} />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleApproval}
                disabled={approvalSaving || (approvalAction === 'reject' && !approvalNotes.trim())}
                className={`flex-1 px-4 py-2 rounded font-medium text-white transition-colors disabled:opacity-50 ${
                  approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                  approvalAction === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                  'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {approvalSaving ? 'Saving…' : approvalAction === 'submit' ? 'Submit for Approval' : approvalAction === 'approve' ? 'Approve' : 'Reject'}
              </button>
              <button onClick={() => setApprovalBudget(null)} className="btn-secondary flex-1" disabled={approvalSaving}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
